import { FC, ComponentProps } from 'react';
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import { faAward } from '@fortawesome/pro-regular-svg-icons';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Flex, BoxV2 as Box } from 'portal-commons';

import { sortMnoList, toastFlashMessage } from '@/utils/index';
import { CampaignVerification } from '@/apis/core';
import { humanize } from '@/utils/string';
import StatusIndicator from './StatusIndicator';
import VettingScoreTooltip from '../VettingScoreTooltip';

export interface MnoStatus {
  provider: string;
  qualify: string;
  mnoReview: string;
  billingCategory: string;
  fallbackSupported: string;
  status: string;
  verification?: CampaignVerification | null;
  dca?: string;
}

interface MnoStatusMatrixProps {
  mnoStatus: Array<MnoStatus>;
}

interface ColumnProps extends ComponentProps<typeof Flex> {
  title?: string;
  width?: string;
  children?: React.ReactNode;
  gap?: boolean;
  bold?: boolean;
  icon?: FontAwesomeIconProps['icon'];
}

const Column: FC<ColumnProps> = ({
  title = '',
  width = '10%',
  children,
  gap = false,
  bold = true,
  icon,
  sx,
  ...restProps
}) => (
  <Flex
    sx={{
      flexDirection: 'column',
      width,
      mr: gap ? '4px' : 0,
      fontSize: 'H200',
      fontWeight: 'bold',
      ...sx,
    }}
    {...restProps}
  >
    <Flex
      sx={{
        color: 't.blue100',
        bg: 't.paleBlue300',
        textAlign: 'center',
        fontSize: '12px',
        height: '45px',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon && (
        <Box sx={{ mr: 'xxs' }}>
          <FontAwesomeIcon icon={icon} size="xl" />
        </Box>
      )}
      {title}
    </Flex>
    <Flex
      sx={{
        height: '45px',
        alignItems: 'center',
        justifyContent: 'center',
        bg: 't.black05',
        color: 't.black60',
        fontWeight: bold ? 'bold' : 'normal',
      }}
    >
      {children}
    </Flex>
  </Flex>
);

const CarrierRow = ({ data }: { data: MnoStatus }) => {
  const columnWidth1 = '11.5%';
  const columnWidth2 = '15.36%';
  const { verification } = data;

  const getVerificationStatusIndicatorColor = () => {
    const status = verification?.verificationStatus?.toLowerCase();
    if (status === 'verified') {
      return 'success';
    } else if (status === 'unverified') {
      return 'error';
    }
    return 'neutral';
  };
  return (
    <Flex sx={{ flex: 1 }}>
      <Flex
        sx={{
          width: columnWidth1,
          bg: 't.blue100',
          color: 'white',
          fontSize: 'H200',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
        }}
        data-testid="mnoStatusMatrixMno"
      >
        {data.provider}
      </Flex>
      <Column
        gap
        icon={faAward}
        title="QUALIFY"
        width={columnWidth1}
        data-testid="mnoStatusMatrixQualify"
      >
        {data.qualify}
      </Column>
      <Column
        title="BILLING CATEGORY"
        width={columnWidth2}
        bold={false}
        data-testid="mnoStatusMatrixBillingCategory"
      >
        {data.billingCategory}
      </Column>
      <Column
        title="FALLBACK SUPPORTED"
        width={columnWidth2}
        bold={false}
        data-testid="mnoStatusMatrixFallbackSupported"
      >
        {data.fallbackSupported}
      </Column>
      <Column
        title="STATUS"
        width={columnWidth2}
        data-testid="mnoStatusMatrixStatus"
      >
        {data.status && (
          <StatusIndicator
            variant={
              data.status.toLocaleLowerCase() === 'active' ? 'success' : 'error'
            }
          />
        )}
        {data.status || 'N/A'}
      </Column>
      <Column
        title="VERIFICATION STATUS"
        width={columnWidth2}
        data-testid="mnoStatusMatrixVerificationStatus"
      >
        <Flex sx={{ alignItems: 'center' }}>
          {!!verification?.verificationStatus && (
            <StatusIndicator variant={getVerificationStatusIndicatorColor()} />
          )}
          {humanize(verification?.verificationStatus) || 'N/A'}
          {(!!verification?.vettingId || !!verification?.vettingToken) && (
            <VettingScoreTooltip
              data={verification.reasons || []}
              interactive
              placement="left-start"
              footer={
                <CopyableVettingIdToken
                  vettingId={verification.vettingId}
                  vettingToken={verification.vettingToken}
                />
              }
              sx={{ ml: 'xxs' }}
              popperSx={{
                left: '0',
              }}
            />
          )}
        </Flex>
      </Column>
      <Column
        title="ELECTED DCA"
        width={columnWidth2}
        bold={false}
        data-testid="mnoStatusMatrixElectedDca"
      >
        {data.dca}
      </Column>
    </Flex>
  );
};

const CopyableVettingIdToken = ({
  vettingId,
  vettingToken,
}: Pick<CampaignVerification, 'vettingId' | 'vettingToken'>) => {
  return (
    <Flex
      sx={{
        alignItems: 'center',
        gap: '27px',
        fontSize: 'H200',
        width: '100%',
        py: 's',
        '[role="button"]': {
          cursor: 'pointer',
          textDecoration: 'underline',
          color: 't.blue100',
          marginLeft: '4px',
        },
        '> *': {
          flex: '1',
        },
      }}
    >
      <VettingField
        label="Vetting ID"
        value={vettingId}
        successMsg="Vetting ID has been copied to your clipboard"
        buttonTestId="vettingIdCopyButton"
      />

      <VettingField
        label="Vetting Token"
        value={vettingToken}
        successMsg="Vetting Token has been copied to your clipboard"
        buttonTestId="vettingTokenCopyButton"
      />
    </Flex>
  );
};

const VettingField = ({
  label,
  value,
  successMsg,
  buttonTestId,
}: {
  label: string;
  value: string;
  successMsg: string;
  buttonTestId: string;
}) => {
  return (
    <Flex>
      <Box as="label" sx={{ minWidth: 'fit-content', maxWidth: 'fit-content' }}>
        {label}:
      </Box>
      <Box
        sx={{
          flexShrink: '1',
          flexBasis: 'auto',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ml: '6px',
        }}
      >
        {value || 'N/A'}
      </Box>
      {!!value?.length && (
        <CopyToClipboard
          text={value}
          onCopy={(_: string, success: boolean) => {
            success && toastFlashMessage(successMsg, 'success');
          }}
        >
          <div role="button" data-testid={buttonTestId}>
            copy
          </div>
        </CopyToClipboard>
      )}
    </Flex>
  );
};

const MnoStatusMatrix: FC<MnoStatusMatrixProps> = ({ mnoStatus }) => {
  return (
    <Flex
      data-testid="mnoStatusMatrix"
      sx={{ flexDirection: 'column', width: '100%', gap: '8px' }}
    >
      {Array.isArray(mnoStatus) &&
        sortMnoList(mnoStatus, ['rulesEngineResultsJson', 'mno']).map(
          (item: MnoStatus) => <CarrierRow data={item} />
        )}
    </Flex>
  );
};

export default MnoStatusMatrix;
